import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { SearchIcon, MenuIcon, XIcon, ShoppingBagIcon, ClipboardListIcon } from 'lucide-react';
import { OCCASIONS } from '../data/smaken';
import { CITY_GUIDES } from '../data/guide';
import { SHOP_CATEGORIES } from '../data/shop';
import { Logo } from './Logo';
import { useRequest } from '../CartContext';
import { useShop } from '../ShopContext';

/** The three branches of the platform, in the order they matter. */
const BRANCHES = [{
  to: '/catering',
  label: 'Catering'
}, {
  to: '/guide',
  label: 'Guiden'
}, {
  to: '/butik',
  label: 'Butiken'
}];
export function Header() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const {
    count: requestCount
  } = useRequest();
  const {
    count: shopCount
  } = useShop();
  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);
  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(query.trim() ? `/catering?q=${encodeURIComponent(query.trim())}` : '/catering');
  };
  const navLinkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `font-display text-xl uppercase leading-none transition-colors duration-150 ease-out ${isActive ? 'text-rod' : 'text-black hover:text-rod'}`;
  const mobileSub = (to: string, label: string) => <li key={to + label} data-id="element-315" data-mp="3ad110bmfri3m">
      <Link to={to} className="block border-b border-black/8 py-2.5 text-[12px] font-bold uppercase tracking-wide2 text-black/70" data-id="element-316" data-mp="35lj1zt3z3rla">
        {label}
      </Link>
    </li>;
  return <header className="sticky top-0 z-50 border-b border-black/12 bg-papper" data-id="element-317" data-mp="1w61n64ukfua">
      <div className="relative mx-auto flex h-[74px] max-w-page items-center gap-6 px-5 md:px-8 lg:h-[98px]" data-id="element-318" data-mp="1yafogwgmv8a">
        <button onClick={() => setOpen(v => !v)} aria-label={open ? 'Stäng meny' : 'Meny'} aria-expanded={open} className="flex h-9 w-9 shrink-0 items-center justify-center border border-black/15 transition-colors duration-150 ease-out hover:border-black lg:hidden" data-id="element-319" data-mp="1rdl5alipvq1m">
          {open ? <XIcon className="h-4 w-4" data-id="element-320" data-mp="1mm301l9nv3au" /> : <MenuIcon className="h-4 w-4" data-id="element-321" data-mp="1ml9zj21apa15" />}
        </button>

        <Link to="/" aria-label="Smaken som baken — till startsidan" className="absolute left-1/2 max-w-[48vw] -translate-x-1/2 lg:static lg:max-w-none lg:translate-x-0" data-id="element-322" data-mp="18mk2tnie1cdb">
          <Logo className="-my-8 h-28 lg:-my-[4.5rem] lg:-ml-12 lg:h-64" data-id="element-323" data-mp="ypxm9ax3h7h3" />
        </Link>

        <nav aria-label="Huvudmeny" className="hidden flex-1 items-center gap-7 lg:flex" data-id="element-324" data-mp="13n786gl9z106">
          {BRANCHES.map(branch => <NavLink key={branch.to} to={branch.to} className={navLinkClass} data-id="element-325" data-mp="2t3ygfihp96au">
              {branch.label}
            </NavLink>)}
          <NavLink to="/om" className={({
          isActive
        }) => `text-[12px] font-semibold uppercase tracking-wide2 transition-colors duration-150 ease-out ${isActive ? 'text-rod' : 'text-black/55 hover:text-rod'}`} data-id="element-326" data-mp="2y18efmj5mqaa">
            Om oss
          </NavLink>
          <NavLink to="/upptack" className={({
          isActive
        }) => `text-[12px] font-semibold uppercase tracking-wide2 transition-colors duration-150 ease-out ${isActive ? 'text-rod' : 'text-black/55 hover:text-rod'}`} data-id="element-327" data-mp="w4pxi71nxg5m">
            Upptäck alla
          </NavLink>
        </nav>

        <div className="ml-auto flex items-center gap-2 md:gap-3" data-id="element-328" data-mp="106ktqx4ql3cj">
          <button onClick={() => setSearchOpen(v => !v)} aria-label="Sök" aria-expanded={searchOpen} className="hidden h-9 w-9 items-center justify-center border border-black/15 transition-colors duration-150 ease-out hover:border-black hover:bg-black hover:text-papper sm:flex" data-id="element-329" data-mp="108ffuerqv1i7">
            <SearchIcon className="h-4 w-4" data-id="element-330" data-mp="3guyplnh2n260" />
          </button>

          <Link to="/kassa" aria-label={`Varukorg, ${shopCount} ${shopCount === 1 ? 'vara' : 'varor'}`} className="relative flex h-9 w-9 items-center justify-center border border-black/15 transition-colors duration-150 ease-out hover:border-black hover:bg-black hover:text-papper" data-id="element-331" data-mp="3bwnhbh6fczpw">
            <ShoppingBagIcon className="h-4 w-4" data-id="element-332" data-mp="2cwbkspa5er2s" />
            {shopCount > 0 && <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[1rem] items-center justify-center bg-blaa px-1 text-[10px] font-bold text-papper" data-id="element-333" data-mp="2cv0heqkm7lc7">
                {shopCount}
              </span>}
          </Link>

          <Link to="/forfragan" aria-label={`Din cateringförfrågan, ${requestCount} ${requestCount === 1 ? 'post' : 'poster'}`} className="flex h-9 items-center gap-2 border border-black bg-black px-3 text-papper transition-colors duration-150 ease-out hover:border-rod hover:bg-rod" data-id="element-334" data-mp="1p70nxivx5gtx">
            <ClipboardListIcon className="h-4 w-4" data-id="element-335" data-mp="11ho6xwuco4h2" />
            <span className="text-[11px] font-bold uppercase tracking-wide2" data-id="element-336" data-mp="11h6ezceq0cey">
              <span className="hidden sm:inline" data-id="element-337" data-mp="15i8430exb837">Förfrågan </span>
              {requestCount}
            </span>
          </Link>

        </div>
      </div>

      {searchOpen && <div className="border-t border-black/12 bg-papper" data-id="element-338" data-mp="t05s49so52sr">
          <form onSubmit={submitSearch} className="mx-auto flex max-w-page items-center gap-3 px-5 py-4 md:px-8" data-id="element-339" data-mp="sykv8divt7rq">
            <SearchIcon className="h-5 w-5 shrink-0 text-black/40" aria-hidden="true" data-id="element-340" data-mp="k0sed4xihs28" />
            <input autoFocus value={query} onChange={e => setQuery(e.target.value)} placeholder="Frukost, fikakorg, buffé, tårta…" aria-label="Sök catering" className="w-full bg-transparent font-display text-xl outline-none placeholder:text-black/30 sm:text-2xl" data-id="element-341" data-mp="f1fw6xhl595z" />
            <button type="submit" className="shrink-0 bg-rod px-5 py-2.5 text-[11px] font-bold uppercase tracking-wide2 text-papper transition-colors duration-150 ease-out hover:bg-black" data-id="element-342" data-mp="17ev3x35va2g">
              Sök
            </button>
          </form>
        </div>}

      {open && <nav aria-label="Mobilmeny" className="max-h-[calc(100vh-74px)] overflow-y-auto overscroll-contain border-t border-black/12 bg-papper lg:hidden" data-id="element-343" data-mp="3irx5mjd4o9zo">
          <ul className="mx-auto max-w-page px-5 py-3 md:px-8" data-id="element-344" data-mp="33xacgyots9di">
            <li data-id="element-345" data-mp="38uiy5996mvji">
              <Link to="/catering" className="block py-3 font-display text-3xl uppercase text-rod" data-id="element-346" data-mp="38w3k4ahfl65r">
                Catering
              </Link>
            </li>
            <li data-id="element-347" data-mp="5c8wnwjdi0vg">
              <ul className="mb-4" data-id="element-348" data-mp="5cr3to7kpzi5">
                {OCCASIONS.map(o => mobileSub(`/catering?tillfalle=${o}`, o))}
              </ul>
            </li>

            <li data-id="element-349" data-mp="3rsd57hqr2wz9">
              <Link to="/guide" className="block py-3 font-display text-3xl uppercase" data-id="element-350" data-mp="3rs3h5zp9mzw7">
                Guiden
              </Link>
            </li>
            <li data-id="element-351" data-mp="jaywhjxkueni">
              <ul className="mb-4" data-id="element-352" data-mp="o3ryi01nv8vw">
                {CITY_GUIDES.map(g => mobileSub(`/guide/${g.city.toLowerCase()}`, g.city))}
              </ul>
            </li>

            <li data-id="element-353" data-mp="28scjwur8a6w4">
              <Link to="/butik" className="block py-3 font-display text-3xl uppercase" data-id="element-354" data-mp="28rucr331289f">
                Butiken
              </Link>
            </li>
            <li data-id="element-355" data-mp="2iiyqjzgyvn2o">
              <ul className="mb-4" data-id="element-356" data-mp="2ijgxpr563lpd">{SHOP_CATEGORIES.map(c => mobileSub(`/butik?kategori=${c}`, c))}</ul>
            </li>

            <li data-id="element-357" data-mp="2dn078qahwv94">
              <Link to="/om" className="block border-t border-black/10 py-3.5 text-[12px] font-bold uppercase tracking-wide2" data-id="element-358" data-mp="1pvbcl3a7bup0">
                Om Småken
              </Link>
              <Link to="/upptack" className="block border-t border-black/10 py-3.5 text-[12px] font-bold uppercase tracking-wide2" data-id="element-359" data-mp="1ywiysmzg152q">
                Upptäck alla ställen
              </Link>
            </li>
            <li data-id="element-360" data-mp="3ad984ez5yd0k">
              <Link to="/bli-en-del" className="block py-3.5 text-[12px] font-bold uppercase tracking-wide2 text-rod" data-id="element-361" data-mp="369l05v6mdfes">
                Bli en del av Småken →
              </Link>
            </li>
          </ul>
        </nav>}
    </header>;
}